import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import React, { useCallback, useMemo } from 'react';
import Modal from 'src/components/Modal';
import Button from 'src/components/design-system/Button';
import PermissionGuard from 'src/components/guards/PermissionGuard';
import { CanManageOfflineSuppliers } from 'src/constants/permissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useEnterInvoices from 'src/hooks/useEnterInvoices';

import usePermission from 'src/hooks/usePermissions';
import useRedirect from 'src/hooks/useRedirect';
import AttachDocumentWizard from 'src/sections/attach_document/AttachDocumentWizard';
import OfflineSupplierGrid from 'src/sections/enter_receipt/OfflineSupplierGrid';
import useGetManagerApi from 'src/services/useGetManagerApi';
import { IOfflineSupplier } from 'src/types/OfflineSupplier';

interface IProps {
  openModal: boolean;
  closeModal: () => void;
}

const CreateReceiptWithInvoice: React.FC<IProps> = ({ openModal, closeModal }) => {
  const { managers } = useGetManagerApi();
  const canManageOfflineSuppliers = usePermission({ permissions: CanManageOfflineSuppliers });
  const { redirect, getUrl } = useRedirect();
  const {
    selectedSupplierInvoiceRow,
    setSelectedSupplierInvoiceRow,
    handleOcrInvoice,
    isOcrCompany,
    isExistingOcrCallLoading,
  } = useEnterInvoices();

  const { value: openAttachModal, toggle: toggleAttachModal } = useBoolean(false);

  const handleSelectedSupplier = useCallback(
    (data: IOfflineSupplier) => {
      setSelectedSupplierInvoiceRow(data);
    },
    [setSelectedSupplierInvoiceRow]
  );

  const isCreateNewSupplierDisabled = useMemo(
    () => canManageOfflineSuppliers && !!selectedSupplierInvoiceRow?.selected,
    [canManageOfflineSuppliers, selectedSupplierInvoiceRow?.selected]
  );

  const disableCreatePoOrInvoiceButton = useMemo(
    () => !selectedSupplierInvoiceRow?.selected,
    [selectedSupplierInvoiceRow?.selected]
  );

  const handleCreateInvoice = useCallback(async () => {
    if (isOcrCompany) {
      handleOcrInvoice(selectedSupplierInvoiceRow.offlineSupplierId, toggleAttachModal);
    } else {
      toggleAttachModal();
    }
    closeModal();
  }, [
    closeModal,
    handleOcrInvoice,
    isOcrCompany,
    selectedSupplierInvoiceRow.offlineSupplierId,
    toggleAttachModal,
  ]);

  const handleNewSupplier = useCallback(() => {
    redirect(getUrl('manageSupplier'), false, true);
    closeModal();
  }, [closeModal, getUrl, redirect]);

  const offlineSupplier = useMemo(
    () => <OfflineSupplierGrid setSelectedSupplier={handleSelectedSupplier} />,
    [handleSelectedSupplier]
  );
  const managerName = useMemo(
    () => (managers ? managers[0]?.contact.split('-')?.[0] || 'Manager' : 'Manager'),
    [managers]
  );

  return (
    <div>
      <Modal width="80%" title="No PO Found" visible={openModal} closeModal={closeModal}>
        <div>
          <p className="text-sm font-bold mb-2">
            Choose a supplier for this Invoice/Credit Memo.{' '}
            {canManageOfflineSuppliers
              ? 'If you cannot find your supplier, create a new one.'
              : `If you cannot find your supplier, ask ${managerName}, or "Customer Support" to add the supplier. Once added, return to this screen to continue.`}
          </p>
          <div className="flex justify-center gap-2 mb-1">
            <Button
              color="primary"
              onClick={handleCreateInvoice}
              disabled={disableCreatePoOrInvoiceButton}
              loading={isExistingOcrCallLoading}
            >
              Create Invoice/Credit Memo
            </Button>
            <PermissionGuard permissions={CanManageOfflineSuppliers}>
              <Button
                color="secondary"
                disabled={isCreateNewSupplierDisabled}
                onClick={handleNewSupplier}
              >
                Create New Supplier
              </Button>
            </PermissionGuard>
          </div>
        </div>
        {offlineSupplier}
        <DialogActionsBar>
          <div className="flex items-center justify-between gap-2">
            <Button color="info" onClick={closeModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Modal>

      <AttachDocumentWizard
        openModal={openAttachModal}
        closeModal={toggleAttachModal}
        title="Attach to New Invoice/Credit Memo"
        isNewDocument
      />
    </div>
  );
};

export default CreateReceiptWithInvoice;
